import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FC, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  getSortedCountryDialOptions,
  resolveCountrySelectIso,
} from 'src/utils/countryDialCodes';
import FieldGridContainer from '../fieldGridContainer';

const SelectFieldErrorFallback: FC<{ name: string }> = ({ name }) => {
  const { translateText } = useTranslation();
  return (
    <FormControl fullWidth error>
      <Typography variant="caption" color="error">
        {translateText('common.fieldError', { fieldName: name })}
      </Typography>
    </FormControl>
  );
};

export interface ControlledCountryCodeSelectFieldProps<T extends FieldValues> {
  label: string;
  id: string;
  name: Path<T>;
  control: Control<T>;
  errorMessage?: string;
  isGridItem?: boolean;
  isRequired?: boolean;
  dataTestId?: string;
  size?: 'small' | 'medium';
}

function ControlledCountryCodeSelectFieldInner<T extends FieldValues>(
  props: ControlledCountryCodeSelectFieldProps<T>
) {
  const {
    label,
    id,
    name,
    control,
    errorMessage,
    isRequired = false,
    dataTestId = 'country-code-select-field',
    size = 'small',
  } = props;

  const { translateText } = useTranslation();
  const options = useMemo(() => getSortedCountryDialOptions(), []);

  /** `createPathProxy` yields a string-like Proxy; RHF expects a real string (`name.search`). */
  const fieldName = String(name) as Path<T>;

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <SelectFieldErrorFallback name={fieldName} />
      )}
      onError={(error, info) => {
        console.error(
          'ControlledCountryCodeSelectField:',
          error,
          info,
          fieldName,
          id
        );
      }}
    >
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => {
          const selectedIso2 = resolveCountrySelectIso(field.value, options);

          if (options.length === 0) {
            return (
              <FormControl
                fullWidth
                required={isRequired}
                size={size}
                data-testid={dataTestId}
              >
                <InputLabel id={id} shrink>
                  {label}
                </InputLabel>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {translateText('common.loading')}
                </Typography>
              </FormControl>
            );
          }

          return (
            <FormControl
              fullWidth
              required={isRequired}
              error={!!errorMessage}
              size={size}
              data-testid={dataTestId}
            >
              <InputLabel id={id} shrink>
                {label}
              </InputLabel>
              <Select
                labelId={id}
                id={`${id}-field`}
                label={label}
                name={field.name}
                value={selectedIso2}
                onChange={(e) => {
                  const iso2 = String(e.target.value);
                  const row = options.find((o) => o.iso2 === iso2);
                  if (row) {
                    field.onChange(`+${row.dialCode}`);
                  }
                }}
                onBlur={field.onBlur}
                ref={field.ref}
                displayEmpty={false}
                sx={{
                  '& .MuiSelect-select': {
                    minHeight: '24px',
                    fontSize: {
                      xs: '16px',
                      sm: '14px',
                    },
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 360,
                      '& .MuiMenuItem-root': {
                        minHeight: '44px',
                        fontSize: {
                          xs: '16px',
                          sm: '14px',
                        },
                        padding: {
                          xs: '12px 16px',
                          sm: '8px 16px',
                        },
                      },
                    },
                  },
                }}
              >
                {options.map((opt) => (
                  <MenuItem
                    key={opt.iso2}
                    value={opt.iso2}
                    data-testid={`country-code-option-${opt.iso2}`}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errorMessage ? (
                <FormHelperText error>{errorMessage}</FormHelperText>
              ) : null}
            </FormControl>
          );
        }}
      />
    </ErrorBoundary>
  );
}

function ControlledCountryCodeSelectField<T extends FieldValues>(
  props: ControlledCountryCodeSelectFieldProps<T>
) {
  const { isGridItem = true, dataTestId = 'country-code-select-field' } = props;

  const inner = <ControlledCountryCodeSelectFieldInner {...props} />;

  if (isGridItem) {
    return (
      <FieldGridContainer dataTestId={dataTestId}>{inner}</FieldGridContainer>
    );
  }

  return inner;
}

export default ControlledCountryCodeSelectField;
